import { useConfigurationStore } from "@/store/configuration.js"
import { useAppStateStore } from "@/store/appState.js"
import { useUserSessionStore } from "@/store/userSession.js"

import { eventBus } from "@/plugins/eventBus.js"

import * as accountsApi from "@/api/accountsApi.js"

import * as texts from "@/services/texts.js"
import * as sentryReporter from "@/services/sentryReporter.js"

let pubNubInstance = null
let prefix = null

export const subscribe = async function (channel) {
  const appStateStore = useAppStateStore()
  const configurationStore = useConfigurationStore()
  if (appStateStore.webViewMode) {
    return
  }

  if (!configurationStore.configuration.pubnub) {
    sentryReporter.reportError(new Error("Pubnub configuration not provided"))
    return
  }

  if (!pubNubInstance) {
    await initialize()
    if (!pubNubInstance) {
      return
    }
  }

  pubNubInstance.subscribe({
    channels: [`${prefix}${channel}`]
  })
}

export const unsubscribe = function (channel, { useBeacon } = {}) {
  if (!pubNubInstance) {
    return
  }

  const channelWithPrefix = `${prefix}${channel}`

  if (useBeacon) {
    const configurationStore = useConfigurationStore()
    const uuid = pubNubInstance.getUUID()
    const subscribeKey = configurationStore.configuration.pubnub.subscribe_key
    const leaveUrl = `https://ps.pndsn.com/v2/presence/sub-key/${encodeURIComponent(subscribeKey)}/channel/${encodeURIComponent(
      channelWithPrefix
    )}/leave?uuid=${encodeURIComponent(uuid)}`
    const data = new Blob([], { type: "application/json" })
    navigator.sendBeacon(leaveUrl, data)
  } else {
    pubNubInstance.unsubscribe({
      channels: [channelWithPrefix]
    })
  }
}

export const publishToChannel = async function ({ channel, message }) {
  if (!pubNubInstance) {
    await initialize()
    if (!pubNubInstance) {
      return
    }
  }

  await pubNubInstance.publish({
    message,
    channel: `${prefix}${channel}`
  })
}

export const checkLivestreamChannelOccupancy = async function () {
  if (!pubNubInstance) {
    await initialize()
    if (!pubNubInstance) {
      return
    }
  }

  const configurationStore = useConfigurationStore()
  const livestreamChannel = `${prefix}${configurationStore.configuration.pubnub.channel_livestream}`
  const data = await pubNubInstance.hereNow({
    channels: [livestreamChannel],
    includeUUIDs: true
  })

  const occupiedBySelf = Boolean(data.totalOccupancy === 1 && data.channels[livestreamChannel]?.occupants.map((o) => o.uuid).includes(pubNubInstance.getUUID()))

  if (data.totalOccupancy > 0) {
    if (occupiedBySelf) {
      sentryReporter.reportMessage(
        `[LIVESTREAMING] the same user already live on another device ${configurationStore.configuration.pubnub.channel_livestream}`,
        { key: "livestreaming", value: "user_already_live" }
      )
    } else {
      sentryReporter.reportMessage(`[LIVESTREAMING] other livestream in progress ${configurationStore.configuration.pubnub.channel_livestream}`, {
        key: "livestreaming",
        value: "other_livestream_in_progress"
      })
    }
  }

  return {
    totalOccupancy: data.totalOccupancy,
    occupiedBySelf
  }
}

export const destroy = () => {
  pubNubInstance = null
  initializerPromise = null
}

let initializerPromise

const initialize = async function () {
  if (!initializerPromise) {
    initializerPromise = setPubNub()
  }
  await initializerPromise
}

const setPubNub = async () => {
  try {
    const { default: PubNub } = await import("pubnub")

    const configurationStore = useConfigurationStore()
    const userSessionStore = useUserSessionStore()

    const configuration = configurationStore.configuration
    const messagingUpdatesChannel = userSessionStore.messagingUpdatesChannel
    const user = userSessionStore.user

    let publishKey = null

    const { pubnub_publish_key } = await accountsApi.publishKey()
    publishKey = pubnub_publish_key
    prefix = configuration.pubnub.prefix || ""

    pubNubInstance = new PubNub({
      subscribeKey: configuration.pubnub.subscribe_key,
      publishKey,
      //https://www.pubnub.com/docs/sdks/javascript/api-reference/configuration#uuid
      //https://www.pubnub.com/blog/all-pubnub-sdks-now-require-unique-identifiers-uuids/
      uuid: texts.toMd5(`${configuration.appTitle}-${user.id}`)
    })

    pubNubInstance.addListener({
      message(m) {
        const matches = m.channel.match(/(.*)\.(.*)/)

        const channel = matches ? matches[1] : m.channel
        const id = matches && Number(matches[2])

        switch (channel) {
          case `${prefix}${messagingUpdatesChannel}`:
            if (m.message.type === "message") {
              eventBus.$emit("pn-chat-message-received", {
                message: m.message.object,
                conversationId: m.message.object.conversation_id
              })
            } else if (m.message.type === "message_edit") {
              eventBus.$emit("pn-chat-message-edited", {
                message: m.message.object,
                conversationId: m.message.object.conversation_id
              })
            } else if (m.message.type === "conversation_edit") {
              eventBus.$emit("pn-chat-edited", {
                conversation: m.message.object
              })
            } else if (m.message.type === "notification_counts") {
              eventBus.$emit("pn-notification-counts", m.message.object)
            }
            break
          case `${prefix}${configuration.pubnub.channel_control_v2}`:
            switch (m.message.action) {
              case "Streaming":
                eventBus.$emit("pn-livestream-streaming", {
                  id: Number(m.message.stream_id),
                  groupKey: m.message.group_id,
                  eventId: m.message.event_id ? Number(m.message.event_id) : undefined
                })
                break
              case "PreparingStream":
                eventBus.$emit("pn-livestream-preparing", {
                  id: Number(m.message.stream_id),
                  groupKey: m.message.group_id,
                  eventId: m.message.event_id ? Number(m.message.event_id) : undefined
                })
                break
              case "EndStream":
                eventBus.$emit("pn-livestream-ended", {
                  id: Number(m.message.stream_id)
                })
                break
              case "AudienceUpdated":
                eventBus.$emit("pn-livestream-audience-updated", {
                  id: Number(m.message.stream_id),
                  count: m.message.total_audience,
                  user: m.message.user,
                  action: m.message.event_name
                })
                break
              case "UpdateParticipantRole":
                eventBus.$emit("pn-livestream-change-participant-role", {
                  id: Number(m.message.stream_id),
                  token: m.message.token,
                  role: m.message.role,
                  user: m.message.user
                })
                break
              case "ParticipantHandRaise":
                eventBus.$emit("pn-livestream-participant-hand-raise", {
                  raised: m.message.raised,
                  user: m.message.user
                })
                break
            }
            break
          case `${prefix}livestreams`:
            if (m.message.type === "livestream_messages") {
              eventBus.$emit("pn-livestream-chat-messages-received", m.message.object)
            }
            break
          case `${prefix}posts`:
            eventBus.$emit("pn-post-published", {
              postId: id
            })
            break
          case `${prefix}comments`:
            eventBus.$emit("pn-comment-published", {
              commentId: id
            })
            break
          case `${prefix}users`:
            eventBus.$emit("pn-avatar-published", {
              userId: id,
              avatar: m.message.avatar
            })
            break
          case `${prefix}external_link_requests`:
            eventBus.$emit("pn-web-preview-published", {
              link: m.message.external_link
            })
            break
          case `${prefix}lesson_media`:
            eventBus.$emit("pn-lesson-medium-processed", {
              fileUrl: m.message.file_url
            })
            break
          default:
            eventBus.$emit("pn-other-event", m)
        }
      }
    })
  } catch (err) {
    sentryReporter.reportError(err)
  }
}
